import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Loader2, CalendarX, ChevronLeft } from 'lucide-react';

// Öffentliche, login-freie Stammplatz-Wahl (Zugang per QR-Code aus der
// Beratung / vom Tresen). Mobil-first, gleiche Optik wie die Beratungsseite.
// Der Code ist an einen konkreten Lead gebunden, 24h gültig, max. 1–2 Wahlen.
const P1_RED = '#d1272e';
const API = process.env.REACT_APP_BACKEND_URL;

export default function StammplatzWahlPage() {
  const { code } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState(null);
  const [sel, setSel] = useState(null);          // gewählter Kurs (Objekt)
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState([]);          // bestätigte Kurse
  const [verbleibend, setVerbleibend] = useState(0);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/api/public/stammplatz-wahl/${code}`);
      const d = await res.json();
      if (!res.ok) throw new Error(d?.detail || 'Link ungültig');
      setInfo(d);
      setVerbleibend(d.verbleibend);
    } catch (e) {
      setError(e.message || 'Link ungültig');
    }
    setLoading(false);
  }, [code]);

  useEffect(() => { load(); }, [load]);

  const confirm = async () => {
    if (!sel || submitting) return;
    setSubmitting(true); setError('');
    try {
      const res = await fetch(`${API}/api/public/stammplatz-wahl/${code}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ course_id: sel.id }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.detail || 'Das hat leider nicht geklappt');
      setDone(prev => [...prev, d.kurs]);
      setVerbleibend(d.verbleibend);
      setSel(null);
      if (d.verbleibend > 0) await load();   // Kursliste aktualisieren für 2. Wahl
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(e.message);
      setSel(null);
    }
    setSubmitting(false);
  };

  // Kurse nach Tag gruppieren
  const tage = [];
  if (info?.kurse) {
    const byDay = {};
    for (const k of info.kurse) {
      (byDay[k.day] = byDay[k.day] || []).push(k);
    }
    for (const [day, kurse] of Object.entries(byDay)) tage.push({ day, kurse });
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="stammplatz-wahl-page">
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center justify-center">
        <img src="/logo-black.png" alt="P1 Performance Center" className="h-9 object-contain" />
      </div>

      <div className="max-w-md mx-auto px-4 py-6 pb-16">
        {loading ? (
          <div className="text-center py-24">
            <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: P1_RED }} />
            <p className="text-sm text-gray-500 mt-3">Kurse werden geladen…</p>
          </div>
        ) : error && !info ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center mt-8">
            <CalendarX className="w-10 h-10 mx-auto text-gray-300 mb-3" />
            <h1 className="text-lg font-bold">Link nicht mehr gültig</h1>
            <p className="text-sm text-gray-500 mt-2">{error}</p>
          </div>
        ) : verbleibend <= 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center mt-8" data-testid="stammplatz-done">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3" style={{ color: '#16a34a' }} />
            <h1 className="text-xl font-extrabold">Dein Platz ist gesichert! 🎉</h1>
            {done.map((k, i) => (
              <p key={i} className="text-sm text-gray-700 mt-2 font-semibold">{k.name} · {k.day} {k.start} Uhr</p>
            ))}
            <p className="text-sm text-gray-500 mt-4">
              Du bist ab sofort <b>jede Woche automatisch eingebucht</b>. Kannst du mal nicht kommen,
              sag einfach in der P1 App ab — dein Platz bleibt dir trotzdem erhalten.
            </p>
          </div>
        ) : sel ? (
          <div className="mt-4">
            <button onClick={() => setSel(null)} className="flex items-center gap-1 text-sm text-gray-500 mb-4">
              <ChevronLeft className="w-4 h-4" /> Zurück zur Auswahl
            </button>
            <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center">
              <h2 className="text-lg font-extrabold">{sel.name}</h2>
              <p className="text-sm text-gray-500 mt-1">{sel.day} · {sel.start}–{sel.end} Uhr · jede Woche</p>
              <p className="text-xs text-gray-400 mt-3">
                Dein fester Platz — automatisch jede Woche gebucht. Absagen jederzeit in der P1 App möglich.
              </p>
              {error && <div className="mt-3 text-sm text-white rounded-xl px-4 py-3" style={{ backgroundColor: P1_RED }}>{error}</div>}
              <button
                onClick={confirm}
                disabled={submitting}
                className="w-full mt-5 text-white font-bold rounded-xl py-3.5 text-base disabled:opacity-60"
                style={{ backgroundColor: P1_RED }}
                data-testid="stammplatz-confirm"
              >
                {submitting ? 'Wird gesichert…' : 'Diesen Platz fest sichern'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <h1 className="text-2xl font-extrabold tracking-tight">
                {info.vorname ? `Hi ${info.vorname}!` : 'Dein Stammplatz'}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Wähle {info.max_plaetze > 1 ? `deine ${info.max_plaetze} festen Wochenkurse` : 'deinen festen Wochenkurs'} —
                du bist dann jede Woche automatisch dabei.
              </p>
              {done.length > 0 && (
                <p className="text-xs mt-2 font-semibold" style={{ color: '#16a34a' }}>
                  ✓ {done[done.length - 1].name} gesichert — noch {verbleibend} Wahl frei
                </p>
              )}
            </div>

            {error && <div className="mb-4 text-sm text-white rounded-xl px-4 py-3" style={{ backgroundColor: P1_RED }}>{error}</div>}

            <div className="space-y-4" data-testid="stammplatz-tage">
              {tage.map(t => (
                <div key={t.day} className="bg-white rounded-2xl border border-gray-200 p-4">
                  <div className="text-sm font-bold mb-3">{t.day}</div>
                  <div className="space-y-2">
                    {t.kurse.map(k => (
                      <button
                        key={k.id}
                        onClick={() => k.frei && setSel(k)}
                        disabled={!k.frei}
                        className={`w-full flex items-center justify-between rounded-xl border px-4 py-3 text-left ${
                          k.frei ? 'border-gray-200 hover:border-gray-400 bg-white' : 'border-gray-100 bg-gray-50 opacity-50'
                        }`}
                        data-testid={`kurs-${k.id}`}
                      >
                        <div>
                          <div className="text-sm font-semibold">{k.name}</div>
                          <div className="text-xs text-gray-500">{k.start}–{k.end} Uhr</div>
                        </div>
                        <span className="text-xs font-bold" style={{ color: k.frei ? P1_RED : '#9ca3af' }}>
                          {k.frei ? 'Wählen ›' : 'Voll'}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
